// The thread page, /threads/:id?store= (REQ-2026-10-07-167). Mike, 7 Oct 2026, of the company record: "Conversations
// have no links or substance other than a headline"; by pop-up, "A thread page (Recommended)": every message in full,
// oldest to newest, who wrote it and to whom, attachments named. A thread's title on the company record, and a Press
// and intelligence item drawn from a mail, open here.
//
// Drawn in the company record's grammar (records/account.css): the head plate, then one section per message. What the
// rails keep from the reader is not on the page and nothing stands in its place (D42, R5); a thread they may not read
// at all says the one thing a thread that does not exist says.
import { ActionButton } from '../parts/ActionButton.js';
import { useState } from 'react';
import type { Store } from '../api.js';
import { formatDay } from '../records/account.js';
import { Loading, useLoad } from '../records/shared.js';
import { opensPath } from '../deals/live-words.js';
import { readThread, type ThreadMessage, type ThreadPerson, type ThreadRead } from './api.js';
import { SummaryLines } from './SummaryLines.js';
import '../records/account.css';
import './thread.css';

/** The newest shown before "Show the <n> before them": a long thread opens at its end, where it stands now. */
export const THREAD_FIRST = 12;

export function personWords(p: ThreadPerson | null): string {
  if (!p) return 'Someone';
  return p.name?.trim() || p.address?.trim() || 'Someone';
}

export function toWords(m: Pick<ThreadMessage, 'to' | 'cc'>): string | null {
  const names = (ps: ThreadPerson[]) => ps.map(personWords).join(', ');
  if (m.to.length === 0 && m.cc.length === 0) return null;
  return [m.to.length ? `to ${names(m.to)}` : null, m.cc.length ? `copied to ${names(m.cc)}` : null].filter(Boolean).join(', ');
}

function timeOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function sizeWords(n: number | null): string | null {
  if (n === null || n <= 0) return null;
  return n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1000))} KB`;
}

/** The page's own words for how many messages it holds and over what span. */
export function spanWords(t: Pick<ThreadRead, 'messages' | 'more' | 'firstAt' | 'lastAt'>): string {
  const n = t.messages.length + t.more;
  const first = formatDay(t.firstAt);
  const last = formatDay(t.lastAt);
  const span = first === last ? `on ${first}` : `from ${first} to ${last}`;
  return `${n === 1 ? 'One message' : `${n.toLocaleString('en-GB')} messages`} ${span}`;
}

export function ThreadPage({ store, id, storeId, open }: { store: Store; id: string; storeId: string | null; open: (path: string) => void }) {
  const tenant = storeId ?? store.tenantId;
  const [load, reload] = useLoad(() => readThread(tenant, id), [tenant, id]);
  const [all, setAll] = useState(false);

  if (load.kind === 'loading') return <div className="acct thread"><Loading what="the conversation" /></div>;
  if (load.kind === 'error') {
    return (
      <div className="acct thread">
        <p className="none" role="alert">The conversation did not load. Nothing was changed.</p>
        <p><ActionButton reading type="button" className="hx-link" onClick={reload}>Try again</ActionButton></p>
      </div>
    );
  }
  const t = load.data;
  if (!t) {
    return (
      <div className="acct thread">
        <p className="none">There is no conversation here for you to read.</p>
      </div>
    );
  }
  const hidden = all ? 0 : Math.max(0, t.messages.length - THREAD_FIRST);
  const shown = t.messages.slice(hidden);

  return (
    <div className="acct thread">
      <header className="ahead">
        {t.companies.length
          ? (
            <p className="thread-back">
              {t.companies.map((c, i) => (
                <span key={c.id}>{i ? ', ' : ''}<a className="go" href={`/companies/${encodeURIComponent(c.id)}`} onClick={(e) => { e.preventDefault(); open(`/companies/${encodeURIComponent(c.id)}`); }}>{c.name}</a></span>
              ))}
            </p>
          )
          : null}
        <div className="aname"><h1>{t.title}</h1></div>
        <p className="afacts">{spanWords(t)}</p>
        {/* B2355: Cobrain's two lines on it, where the reader may read every message they were written from. */}
        <SummaryLines summary={t.summary} className="thread-head-sum" />
      </header>

      {hidden > 0 || t.more > 0
        ? (
          <p className="none thread-earlier">
            {hidden > 0
              ? <ActionButton reading type="button" className="hx-link" onClick={() => setAll(true)}>Show the {hidden === 1 ? 'message' : `${hidden} messages`} before {hidden === 1 ? 'it' : 'them'}</ActionButton>
              : null}
            {t.more > 0 ? <span>{hidden > 0 ? ' ' : ''}{t.more === 1 ? 'One older message is' : `${t.more.toLocaleString('en-GB')} older messages are`} not on this page.</span> : null}
          </p>
        )
        : null}

      <ol className="thread-list">
        {shown.map((m) => <Message key={m.id} m={m} store={tenant} anchor={m.id === t.anchorId} open={open} />)}
      </ol>
    </div>
  );
}

function Message({ m, store, anchor, open }: { m: ThreadMessage; store: string; anchor: boolean; open: (path: string) => void }) {
  const to = toWords(m);
  const elsewhere = m.open ? opensPath(m.id, m.open, store) : null;
  return (
    <li className={`sec thread-msg${m.mine ? ' mine' : ''}${anchor ? ' anchor' : ''}`} id={`m-${m.id}`}>
      <div className="sh">
        <h2>{m.mine ? 'You' : personWords(m.from)}</h2>
        {to ? <span className="sl">{to}</span> : null}
        <span className="sr">{formatDay(m.at)}, {timeOf(m.at)}</span>
      </div>
      <div className="sb">
        {m.words
          ? <div className="thread-words">{m.words}</div>
          : <p className="none">Cobrain holds no words of its own for this message.</p>}
        {m.cut ? <p className="none">The message runs on past what this page shows.</p> : null}
        {m.attachments.length
          ? (
            <p className="thread-files">
              {m.attachments.length === 1 ? 'Attached: ' : `${m.attachments.length} attached: `}
              {m.attachments.map((a, i) => <span key={`${a.name}-${i}`}>{i ? ', ' : ''}<b>{a.name}</b>{sizeWords(a.sizeBytes) ? ` (${sizeWords(a.sizeBytes)})` : ''}</span>)}
            </p>
          )
          : null}
        {elsewhere
          ? <p className="thread-also"><a className="go" href={elsewhere} onClick={(e) => { e.preventDefault(); open(elsewhere); }}>{m.open!.page === 'item' ? 'Open it on Triage' : m.open!.page === 'meeting' ? 'Open the meeting' : 'Open the chat'}</a></p>
          : null}
      </div>
    </li>
  );
}
